import { OrderState } from '@tcr-erp/shared';
import { formatVND } from '@/utils/format';
import type { OrderCreditLimitInfo } from './useOrderCreditLimitWarning';

/**
 * TCR-2130 — đơn kênh GT: khách thanh toán 100% trước, nên chỉ người có quyền riêng (xác nhận
 * tiền đã về đúng số) mới duyệt được, và KHÔNG xét hạn mức công nợ. Cùng luật với BE
 * `resolveOrderConfirmRequirement` (`order-confirm-permission.util.ts`).
 */
export const ORDERS_CONFIRM_GT_PREPAID = 'orders.confirm_gt_prepaid';
export const GT_PREPAID_TAG_LABEL = 'GT - Đã thanh toán';
export const GT_PENDING_PAYMENT_TAG_LABEL = 'GT - Chờ thanh toán';
export const GT_CONFIRM_DENIED_TOOLTIP = 'Cần quyền Duyệt đơn hàng kênh GT (đã thanh toán trước)';

/** Đơn thuộc kênh GT (snapshot `Order.b2b_channel` lúc tạo đơn). */
export function isGtPrepaidOrder(order: { b2b_channel?: string | null }): boolean {
  return order.b2b_channel === 'GT';
}

/**
 * Nhãn thanh toán của đơn GT. Lúc lên đơn khách có thể CHƯA trả (`payment_state` = PENDING),
 * chỉ khi kế toán xác nhận đã nhận tiền (BE đặt `payment_state` = PAID) mới là "Đã thanh toán".
 * Trước đây nhãn xanh "Đã thanh toán" hiện cho MỌI đơn GT nên đơn chưa trả vẫn bị hiểu nhầm là đã trả.
 */
export function resolveGtPaymentTag(order: { payment_state?: string | null }): { label: string; color: 'green' | 'gold' } {
  return String(order.payment_state || '').trim() === 'PAID'
    ? { label: GT_PREPAID_TAG_LABEL, color: 'green' }
    : { label: GT_PENDING_PAYMENT_TAG_LABEL, color: 'gold' };
}

export interface ConfirmOrderPromptInput {
  /** `order.credit_approval_status` — cờ BE gắn LÚC TẠO đơn (TCR-1522). */
  creditApprovalStatus?: string | null;
  /** Lý do BE đã soạn sẵn kèm cờ `pending`. */
  creditApprovalReason?: string | null;
  /** Số liệu hạn mức tính tại thời điểm xem, theo tổng đơn HIỆN TẠI. */
  creditWarning: OrderCreditLimitInfo | null;
  /** Người dùng có quyền `b2b.orders.credit_approve` ("Duyệt đơn hàng vượt hạn mức nợ"). */
  canApproveCredit: boolean;
  /** TCR-2130 — đơn kênh GT. */
  isGtPrepaid?: boolean;
  /** TCR-2130 — quyền `orders.confirm_gt_prepaid`. */
  canConfirmGtPrepaid?: boolean;
  /** TCR-2130 — tổng đơn để người duyệt đơn GT đối chiếu với tiền đã về. */
  orderTotal?: number;
}

export interface ConfirmOrderPrompt {
  /**
   * `blocked` = không duyệt được, chỉ báo lỗi. `over-limit` = cảnh báo vượt hạn mức
   * nhưng xác nhận thì vẫn duyệt. `normal` = popup xác nhận thường.
   */
  kind: 'blocked' | 'over-limit' | 'gt-prepaid' | 'normal';
  title: string;
  content: string;
  okText: string;
}

/**
 * TCR-1892 — chọn popup cho nút "Duyệt đơn". Tách thành hàm thuần để test được mà
 * không phải dựng cả trang chi tiết (cùng lý do với `resolveOrderPaymentDisplay`).
 *
 * Hai nguồn dữ liệu, không thay thế nhau:
 *
 * - `credit_approval_status` là cờ BE gắn LÚC TẠO đơn. Đây là cờ điều khiển nghiệp vụ
 *   thật (guard xuất kho 409, trang duyệt `/b2b/credit-approvals`), nên khi cờ bật
 *   `pending` thì giữ nguyên hành vi TCR-1823: không có quyền ⇒ chặn hẳn.
 * - `creditWarning` tính tại thời điểm xem, theo tổng đơn và hạn mức HIỆN TẠI. Cần có vì cờ
 *   không tự tính lại: đơn tạo 302.400đ rồi sửa lên 302.400.000đ vẫn mang `not_required`
 *   (TCR-1892), hạn mức được nâng/hạ sau lúc tạo đơn cũng vậy (TCR-2439). Có số liệu này thì
 *   nó thắng cờ, và vượt hạn mức thì chặn theo quyền như cờ `pending` — TCR-2439 v3 BE
 *   `confirmOrder()` cũng tính lại và trả 409 cho người thiếu quyền vượt hạn mức.
 *
 * Cờ `approved`/`rejected` nghĩa là người có quyền duyệt vượt hạn mức đã xử lý rồi — không hỏi lại.
 */
export function buildConfirmOrderPrompt({
  creditApprovalStatus,
  creditApprovalReason,
  creditWarning,
  canApproveCredit,
  isGtPrepaid,
  canConfirmGtPrepaid,
  orderTotal,
}: ConfirmOrderPromptInput): ConfirmOrderPrompt {
  // TCR-2130 — đơn GT đã trả trước nên bỏ qua cả hai nhánh hạn mức bên dưới, kể cả cờ `pending`
  // còn sót trên đơn cũ (BE gỡ cờ đó khi người có quyền GT duyệt).
  if (isGtPrepaid) {
    if (!canConfirmGtPrepaid) {
      return {
        kind: 'blocked',
        title: 'Không thể duyệt đơn hàng',
        content: 'Bạn cần quyền "Duyệt đơn hàng kênh GT (đã thanh toán trước)" để duyệt đơn hàng này.',
        okText: 'Đã hiểu',
      };
    }
    return {
      kind: 'gt-prepaid',
      title: 'Xác nhận duyệt đơn hàng kênh GT (đã thanh toán trước)?',
      // TCR-2149 — chỉ nêu tổng đơn: phiếu thu chỉ lập được SAU khi duyệt, nên lúc này số "đã
      // thanh toán" luôn là 0 và "còn thiếu" luôn bằng cả đơn — người duyệt đối chiếu sao kê.
      content:
        `Tổng đơn: ${formatVND(orderTotal ?? 0)} — kiểm tra lại tiền đã về trước khi duyệt. ` +
        'Bạn có chắc chắn muốn duyệt đơn hàng này không?',
      okText: 'Duyệt đơn',
    };
  }

  // TCR-2439 v3 — có số liệu HIỆN TẠI thì tin số liệu, không tin cờ lưu lúc tạo đơn: hạn mức có thể
  // được nâng (cờ `pending` lỗi thời) hoặc hạ (cờ `not_required` lỗi thời) sau đó. BE `confirmOrder()`
  // cũng tính lại đúng như vậy. Đơn đã được duyệt/từ chối vượt hạn mức thì không hỏi lại.
  const daXuLy = creditApprovalStatus === 'approved' || creditApprovalStatus === 'rejected';
  if (!daXuLy && creditWarning) {
    if (!creditWarning.isOverLimit) return NORMAL_PROMPT;
    const reason = overLimitReason(creditWarning);
    if (!canApproveCredit) return blockedOverLimit(reason);
    return {
      kind: 'over-limit',
      title: 'Xác nhận duyệt đơn hàng vượt hạn mức công nợ?',
      content: `${reason} Bạn có chắc chắn muốn duyệt đơn hàng này không?`,
      okText: 'Duyệt đơn',
    };
  }

  // Không có số liệu hiện tại (khách cá nhân, chưa tải xong, không có quyền xem công nợ) ⇒ theo cờ.
  if (creditApprovalStatus === 'pending') {
    const reason = creditApprovalReason || 'Khách hàng đang có công nợ vượt hạn mức.';
    if (!canApproveCredit) return blockedOverLimit(reason);
    return {
      kind: 'over-limit',
      title: 'Xác nhận duyệt đơn hàng vượt hạn mức công nợ?',
      content: `${reason} Bạn có chắc chắn muốn duyệt đơn hàng này không?`,
      okText: 'Duyệt đơn',
    };
  }

  return NORMAL_PROMPT;
}

const NORMAL_PROMPT: ConfirmOrderPrompt = {
  kind: 'normal',
  title: 'Xác nhận duyệt đơn hàng?',
  content: 'Đơn hàng sẽ được chuyển sang trạng thái Đã xác nhận (CONFIRMED).',
  okText: 'Xác nhận',
};

/**
 * Ba số tách bạch, cùng cách diễn đạt với lý do BE soạn (TCR-1862): hạn mức, số VƯỢT QUÁ, và công
 * nợ dự kiến — thiếu vế nào người đọc cũng không biết con số đang nhìn là gì.
 */
function overLimitReason(creditWarning: OrderCreditLimitInfo): string {
  return `Hạn mức tín dụng: ${formatVND(creditWarning.creditLimit)}. ` +
    `Vượt hạn mức: ${formatVND(creditWarning.excess)}. ` +
    `Công nợ dự kiến sau khi duyệt đơn hàng là: ${formatVND(creditWarning.debtAfter)}.`;
}

function blockedOverLimit(reason: string): ConfirmOrderPrompt {
  return {
    kind: 'blocked',
    title: 'Không thể duyệt đơn hàng',
    // TCR-1974 — nêu quyền còn thiếu, không gọi tên role: role nào được tick quyền này đều duyệt được.
    content: `${reason} Bạn cần quyền "Duyệt đơn hàng vượt hạn mức nợ" để duyệt đơn hàng này.`,
    okText: 'Đã hiểu',
  };
}

export interface CanConfirmOrderInput {
  state?: string | null;
  /** Đơn đã từng được duyệt (có `confirmed_at` hoặc lịch sử CONFIRMED). */
  isEverConfirmed: boolean;
  creditApprovalStatus?: string | null;
  /** Quyền `orders.confirm` — "Duyệt đơn hàng trong hạn mức nợ". */
  canConfirmWithinLimit: boolean;
  /** Quyền `b2b.orders.credit_approve` — "Duyệt đơn hàng vượt hạn mức nợ". */
  canConfirmOverLimit: boolean;
  /**
   * TCR-2439 v3 — đơn VƯỢT hạn mức theo số liệu HIỆN TẠI (`useOrderCreditLimitWarning`).
   * `null`/`undefined` = chưa có số liệu ⇒ xét theo cờ lưu trên đơn.
   */
  isOverLimitNow?: boolean | null;
}

/**
 * TCR-1974 — nút "Duyệt đơn" chỉ hiện với người THỰC SỰ duyệt được đơn đang xem, khớp đúng
 * guard BE `confirmOrder()`: đơn đang chờ duyệt vượt hạn mức (cờ `pending`) chỉ cần quyền
 * vượt hạn mức, mọi đơn còn lại chỉ cần quyền trong hạn mức. Trước đây đơn `pending` hiện nút
 * cho cả người chỉ có `orders.confirm` (bấm mới bị chặn) ⇒ đơn "đổ về" cả NVKD lẫn kế toán.
 */
export function canConfirmOrder({
  state,
  isEverConfirmed,
  creditApprovalStatus,
  canConfirmWithinLimit,
  canConfirmOverLimit,
  isOverLimitNow,
}: CanConfirmOrderInput): boolean {
  if (state !== OrderState.PENDING || isEverConfirmed) return false;
  // TCR-2439 v3 — khớp BE `confirmOrder()`: cờ lưu lúc tạo đơn được tính lại theo hạn mức hiện tại.
  const daXuLy = creditApprovalStatus === 'approved' || creditApprovalStatus === 'rejected';
  if (!daXuLy && isOverLimitNow === true) return canConfirmOverLimit;
  if (!daXuLy && isOverLimitNow === false && creditApprovalStatus === 'pending') {
    // BE: trong hạn mức + `orders.confirm` ⇒ gỡ cờ; chỉ có quyền vượt hạn mức ⇒ vẫn ghi `approved`.
    return canConfirmWithinLimit || canConfirmOverLimit;
  }
  return creditApprovalStatus === 'pending' ? canConfirmOverLimit : canConfirmWithinLimit;
}

export interface ConfirmOrderButtonInput extends CanConfirmOrderInput {
  /** `order.b2b_channel` (MT/GT/SI). */
  b2bChannel?: string | null;
  /** Quyền `orders.confirm_gt_prepaid` — "Duyệt đơn hàng kênh GT (đã thanh toán trước)". */
  canConfirmGtPrepaid: boolean;
}

/**
 * TCR-2130 — trạng thái nút "Duyệt đơn". Đơn kênh GT: người không có quyền vẫn THẤY nút nhưng
 * mờ kèm lý do (ticket yêu cầu), để biết đơn đang chờ ai. Đơn kênh khác giữ nguyên luật
 * `canConfirmOrder` (ẩn hẳn khi không duyệt được).
 */
export function resolveConfirmOrderButton(input: ConfirmOrderButtonInput): {
  visible: boolean;
  disabledReason: string | null;
} {
  if (input.b2bChannel === 'GT') {
    if (input.state !== OrderState.PENDING || input.isEverConfirmed) {
      return { visible: false, disabledReason: null };
    }
    return {
      visible: true,
      disabledReason: input.canConfirmGtPrepaid ? null : GT_CONFIRM_DENIED_TOOLTIP,
    };
  }
  return { visible: canConfirmOrder(input), disabledReason: null };
}

export interface CanReturnDiscountReviewInput extends ConfirmOrderButtonInput {
  /** `order.discount_review.has_deviation` — đơn có CK khác chính sách. */
  hasDeviation: boolean;
  /** `order.discount_review.returned` — đã bị trả lại, chờ NVKD sửa. */
  returned: boolean;
}

/**
 * TCR-2195 — "Trả lại đơn" (CK khác chính sách) là một nhánh của duyệt: chỉ người BẤM ĐƯỢC nút
 * "Duyệt đơn" của chính đơn này mới trả lại được. Trước đây chỉ cần 1 trong 3 quyền duyệt bất kỳ
 * ⇒ NVKD GT/MT (có `orders.confirm` để duyệt đơn MT) vẫn trả lại được đơn GT. Khớp BE
 * `returnForDiscountReview()` (`resolveOrderConfirmRequirement`).
 */
export function canReturnDiscountReview({ hasDeviation, returned, ...confirmInput }: CanReturnDiscountReviewInput): boolean {
  if (!hasDeviation || returned) return false;
  const button = resolveConfirmOrderButton(confirmInput);
  return button.visible && !button.disabledReason;
}
